import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react'
import { MapElement } from '../../../types'
import {
  MapCanvasProps,
  MapCanvasHandle,
  Point,
  ContextMenuState,
} from './types'
import { Tool } from '../ToolsPanel'
import {
  CanvasContainer,
  CanvasWrapper,
  Canvas,
  CanvasOverlay,
  ZoomControls,
  ZoomButton,
  CoordinatesDisplay,
} from './styled'
import {
  getCanvasCoordinates,
  getCursorClass,
  createElementFromDrawing,
  isPointInLabel,
  getElementLayer,
} from './helpers'
import {
  drawElement,
  drawPreview,
  drawPolygonPreview,
  drawMeasurement,
} from './drawingHelpers'
import {
  handlePolygonClick,
  handleTextToolClick,
  handlePoiToolClick,
  handleSelectToolClick,
  handleMeasureToolClick,
  handleDragMove,
  handleZoomIn,
  handleZoomOut,
  addVertex,
  removeVertex,
} from './eventHandlers'
import { ContextMenu } from './components/ContextMenu'
import {
  drawGrid,
  drawBackgroundImage,
  drawHandles,
  drawLabelHandle,
} from '../canvas/drawingRenderers'
import { getHandleAtPoint } from '../canvas/hitDetection'
import { isLineBasedElement, isPointOnLine } from '../canvas/geometryUtils'
import { captureContentThumbnail } from './screenshotHelpers'
import {
  snapPointToAngle,
  drawAngleGuides,
  drawDragFeedback
} from '../canvas/angleSnapping'

const MapCanvas = forwardRef<MapCanvasHandle, MapCanvasProps>(
  (
    {
      elements,
      activeTool,
      selectedElement,
      hoveredElementId,
      isolatedElementId,
      onSelectElement,
      onAddElement,
      onUpdateElement,
      mapSettings,
    },
    ref,
  ) => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const [isDrawing, setIsDrawing] = useState(false)
    const [drawStart, setDrawStart] = useState<Point | null>(null)
    const [zoom, setZoom] = useState(1)
    const [panOffset, setPanOffset] = useState<Point>({ x: 0, y: 0 })
    const [mousePos, setMousePos] = useState<Point>({ x: 0, y: 0 })
    const mousePosRef = useRef<Point>({ x: 0, y: 0 })
    const [isPanning, setIsPanning] = useState(false)
    const [panStart, setPanStart] = useState<Point | null>(null)
    const [measureStart, setMeasureStart] = useState<Point | null>(null)
    const [measureEnd, setMeasureEnd] = useState<Point | null>(null)
    const [isDragging, setIsDragging] = useState(false)
    const [dragStart, setDragStart] = useState<Point | null>(null)
    const [dragElementId, setDragElementId] = useState<string | null>(null)
    const [dragHandle, setDragHandle] = useState<
      | 'start'
      | 'end'
      | 'whole'
      | 'vertex'
      | 'label'
      | 'controlPoint1'
      | 'controlPoint2'
      | null
    >(null)
    const [hoveredElement, setHoveredElement] = useState<MapElement | null>(
      null,
    )
    const [hoveredHandle, setHoveredHandle] = useState<
      | 'start'
      | 'end'
      | 'vertex'
      | 'label'
      | 'controlPoint1'
      | 'controlPoint2'
      | null
    >(null)
    const [hoveredVertexIndex, setHoveredVertexIndex] = useState<number>(-1)
    const [dragVertexIndex, setDragVertexIndex] = useState<number>(-1)
    const [selectedHandle, setSelectedHandle] = useState<
      'start' | 'end' | 'vertex' | 'controlPoint1' | 'controlPoint2' | null
    >(null)
    const [selectedVertexIndex, setSelectedVertexIndex] = useState<number>(-1)
    const [polygonVertices, setPolygonVertices] = useState<Point[]>([])
    const [isPolygonDrawing, setIsPolygonDrawing] = useState(false)
    const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(
      null,
    )
    const [isShiftPressed, setIsShiftPressed] = useState(false)
    const [previousTool, setPreviousTool] = useState<Tool | null>(null)
    const [isMiddleMousePanning, setIsMiddleMousePanning] = useState(false)

    // Expose screenshot capture method
    useImperativeHandle(ref, () => ({
      captureScreenshot: () =>
        captureContentThumbnail(canvasRef.current, elements, zoom, panOffset),
    }))

    // Draw elements on canvas
    const drawElements = useCallback(() => {
      const canvas = canvasRef.current
      if (!canvas) return

      const ctx = canvas.getContext('2d')
      if (!ctx) return

      // Clear canvas and apply background color
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Draw background color if set
      if (mapSettings.backgroundColor) {
        ctx.fillStyle = mapSettings.backgroundColor
        ctx.fillRect(0, 0, canvas.width, canvas.height)
      }

      // Apply zoom and pan transformations
      ctx.save()
      ctx.scale(zoom, zoom)
      ctx.translate(panOffset.x, panOffset.y)

      // Draw grid background if enabled
      if (mapSettings.showGrid) {
        drawGrid(
          ctx,
          canvas.width / zoom,
          canvas.height / zoom,
          mapSettings,
          panOffset,
          zoom,
        )
      }

      // Draw background image if set
      if (mapSettings.backgroundImage) {
        drawBackgroundImage(
          ctx,
          canvas.width / zoom,
          canvas.height / zoom,
          mapSettings,
        )
      }

      // Filter and draw elements based on floor visibility
      const visibleElements = elements.filter(element => {
        const elementFloor = element.floor || mapSettings.currentFloor
        const floor = mapSettings.floors.find(f => f.id === elementFloor)
        return floor?.isVisible !== false
      })

      // Sort elements by zIndex for proper layering
      const sortedElements = [...visibleElements].sort(
        (a, b) => (a.zIndex || 0) - (b.zIndex || 0),
      )

      // Get layer visibility settings
      const layerVisibility = mapSettings.layerVisibility || {
        architecture: 'show',
        fixtures: 'show',
        annotations: 'show',
        measurements: 'show',
      }

      // First pass: Draw all visible elements (without handles)
      sortedElements.forEach(element => {
        // Skip if element is explicitly hidden
        if (element.visible === false) return

        const elementFloor = element.floor || mapSettings.currentFloor
        const floor = mapSettings.floors.find(f => f.id === elementFloor)

        if (floor?.isVisible) {
          // Check layer visibility
          const layer = getElementLayer(element)
          const visibility =
            layerVisibility[layer as keyof typeof layerVisibility]

          if (visibility === 'hide') return // Skip hidden layers

          // Apply opacity for faded layers
          if (visibility === 'fade') {
            ctx.save()
            ctx.globalAlpha = 0.2
          }

          // Dim elements not on current floor
          const isOnCurrentFloor = elementFloor === mapSettings.currentFloor
          const isSelected = selectedElement?.id === element.id
          const isNavigatorHovered = hoveredElementId === element.id

          // Handle isolation mode - fade non-isolated elements
          const isIsolated = isolatedElementId === element.id
          const inIsolationMode = !!isolatedElementId

          if (inIsolationMode && !isIsolated) {
            ctx.save()
            ctx.globalAlpha = 0.15 // Very faded for context
          }

          // Draw highlight effect for navigator-hovered elements
          if (isNavigatorHovered && !isSelected) {
            ctx.save()
            ctx.shadowColor = '#007bff'
            ctx.shadowBlur = 15
            ctx.shadowOffsetX = 0
            ctx.shadowOffsetY = 0
            // Draw a glowing outline
            ctx.strokeStyle = '#007bff'
            ctx.lineWidth = 3
            ctx.globalAlpha = inIsolationMode && !isIsolated ? 0.15 : 0.5
          }

          drawElement(ctx, element, isSelected || isNavigatorHovered, isOnCurrentFloor, mapSettings, elements)

          if (isNavigatorHovered && !isSelected) {
            ctx.restore()
          }

          if (inIsolationMode && !isIsolated) {
            ctx.restore()
          }

          if (visibility === 'fade') {
            ctx.restore()
          }
        }
      })

      // Second pass: Draw all handles on top (ensures bezier handles are always visible)
      if (activeTool === 'select') {
        sortedElements.forEach(element => {
          // Skip if element is explicitly hidden
          if (element.visible === false) return

          // In isolation mode, only show handles for the isolated element
          if (isolatedElementId && element.id !== isolatedElementId) {
            return // Don't show handles for non-isolated elements
          }

          const elementFloor = element.floor || mapSettings.currentFloor
          const floor = mapSettings.floors.find(f => f.id === elementFloor)

          if (floor?.isVisible) {
            const isOnCurrentFloor = elementFloor === mapSettings.currentFloor
            const isSelected = selectedElement?.id === element.id
            const isHovered = hoveredElement?.id === element.id

            // Draw handles for line-based elements (only on current floor)
            if (isOnCurrentFloor) {
              drawHandles(
                ctx,
                element,
                isSelected,
                isHovered,
                hoveredHandle,
                hoveredVertexIndex,
              )

              // Draw label handle if element has a label and is being hovered with label handle
              const hasLabel =
                element.properties.roomName ||
                element.properties.label ||
                (element.type === 'fixture-rect' &&
                  element.properties.fixtureType)
              if (hasLabel && isHovered && hoveredHandle === 'label') {
                drawLabelHandle(ctx, element, true)
              }
            }
          }
        })
      }

      // Draw current drawing if in progress
      if (
        isDrawing &&
        drawStart &&
        activeTool !== 'select' &&
        activeTool !== 'pan' &&
        activeTool !== 'measure'
      ) {
        // Apply angle snapping if shift is pressed
        const snappedPos = isShiftPressed
          ? snapPointToAngle(drawStart, mousePosRef.current)
          : mousePosRef.current

        drawPreview(ctx, activeTool, drawStart, snappedPos)

        // Draw angle guides for line-based tools
        if (['wall', 'door', 'window', 'line', 'fixture-line'].includes(activeTool)) {
          drawAngleGuides(ctx, drawStart, snappedPos, isShiftPressed, mapSettings)
        }
      }

      // Draw polygon drawing in progress
      if (isPolygonDrawing && polygonVertices.length > 0) {
        const lastVertex = polygonVertices[polygonVertices.length - 1]
        const snappedPos = isShiftPressed
          ? snapPointToAngle(lastVertex, mousePosRef.current)
          : mousePosRef.current

        drawPolygonPreview(ctx, polygonVertices, snappedPos)

        // Draw angle guides for current edge
        drawAngleGuides(ctx, lastVertex, snappedPos, isShiftPressed, mapSettings)
      }

      // Draw measurement line if active
      if (
        (measureStart || (activeTool === 'measure' && drawStart)) &&
        (measureEnd || mousePosRef.current)
      ) {
        const startPoint = measureStart || drawStart
        const endPoint = measureEnd || mousePosRef.current
        drawMeasurement(ctx, startPoint, endPoint, mapSettings)
      }

      // Draw angle guides during dragging
      if (isDragging && dragStart && dragElementId && isShiftPressed) {
        const dragElement = elements.find(el => el.id === dragElementId)
        if (dragElement) {
          // For whole element dragging, show angle from element position
          if (dragHandle === 'whole' || dragHandle === 'label' || !dragHandle) {
            const currentPos = {
              x: dragElement.position.x + (mousePosRef.current.x - dragStart.x),
              y: dragElement.position.y + (mousePosRef.current.y - dragStart.y)
            }
            drawAngleGuides(ctx, dragElement.position, currentPos, true, mapSettings)
          } else if (dragHandle !== 'start' && dragHandle !== 'end') {
            // For non-line handle dragging (like vertex), show angle from drag start
            drawAngleGuides(ctx, dragStart, mousePosRef.current, true, mapSettings)
          }
          // Skip drawAngleGuides for 'start' and 'end' handles to avoid duplicate flyout
        }
      }

      // Draw drag feedback for line points (angle and distance flyout)
      if (isDragging && dragElementId && (dragHandle === 'start' || dragHandle === 'end')) {
        const dragElement = elements.find(el => el.id === dragElementId)
        if (dragElement && dragElement.properties.endPosition) {
          // Determine the pivot point (the opposite end of the line)
          const pivotPoint = dragHandle === 'start'
            ? dragElement.properties.endPosition
            : dragElement.position

          // Apply snapping if shift is pressed
          const targetPoint = isShiftPressed
            ? snapPointToAngle(pivotPoint, mousePosRef.current)
            : mousePosRef.current

          // Draw the feedback at current (possibly snapped) position
          drawDragFeedback(ctx, pivotPoint, targetPoint, mapSettings, isShiftPressed)
        }
      }

      ctx.restore()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      elements,
      selectedElement,
      zoom,
      panOffset,
      isDrawing,
      drawStart,
      activeTool,
      mapSettings,
      hoveredElement,
      hoveredHandle,
      polygonVertices,
      isPolygonDrawing,
      hoveredVertexIndex,
      measureStart,
      measureEnd,
      isDragging,
      dragStart,
      dragElementId,
      dragHandle,
      isShiftPressed,
    ])

    // Handle canvas resize
    useEffect(() => {
      const resizeCanvas = () => {
        const canvas = canvasRef.current
        if (!canvas) return

        const container = canvas.parentElement
        if (!container) return

        canvas.width = container.clientWidth
        canvas.height = container.clientHeight
        drawElements()
      }

      resizeCanvas()
      window.addEventListener('resize', resizeCanvas)
      return () => window.removeEventListener('resize', resizeCanvas)
    }, [drawElements])

    // Redraw when elements or other dependencies change
    useEffect(() => {
      drawElements()
    }, [drawElements])

    // Listen for image load events to trigger re-render
    useEffect(() => {
      const handleImageLoaded = () => {
        drawElements()
      }

      window.addEventListener('imageLoaded', handleImageLoaded)
      return () => window.removeEventListener('imageLoaded', handleImageLoaded)
    }, [drawElements])

    const handleMouseDown = (e: React.MouseEvent) => {
      // Handle middle mouse button for panning
      if (e.button === 1) {
        e.preventDefault()
        setIsMiddleMousePanning(true)
        setIsPanning(true)
        setPanStart({ x: e.clientX, y: e.clientY })
        return
      }

      const point = getCanvasCoordinates(
        e.clientX,
        e.clientY,
        canvasRef.current,
        panOffset,
        zoom,
        elements,
        mapSettings,
      )

      // Close context menu when clicking
      setContextMenu(null)

      // Only process left click for tools
      if (e.button !== 0) return

      if (activeTool === 'pan') {
        setIsPanning(true)
        setPanStart({ x: e.clientX, y: e.clientY })
        return
      }

      if (activeTool === 'measure') {
        handleMeasureToolClick(
          point,
          measureStart,
          measureEnd,
          mapSettings,
          setMeasureStart,
          setMeasureEnd,
          setIsDrawing,
          setDrawStart,
          onAddElement,
        )
        return
      }

      if (activeTool === 'select') {
        // Store selected handle for nudging
        if (hoveredHandle) {
          setSelectedHandle(hoveredHandle as any)
          setSelectedVertexIndex(hoveredVertexIndex)
        } else {
          setSelectedHandle(null)
          setSelectedVertexIndex(-1)
        }

        const dragStarted = handleSelectToolClick(
          point,
          elements.filter(el => !isolatedElementId || el.id === isolatedElementId),
          mapSettings,
          onSelectElement,
          setHoveredVertexIndex,
          hoveredVertexIndex,
          setDragStart,
          setIsDragging,
          setDragElementId,
          setDragHandle,
          setDragVertexIndex,
        )

        // Drag was initiated
        return
      }

      if (activeTool === 'text') {
        handleTextToolClick(point, mapSettings, onAddElement)
        return
      }

      if (activeTool === 'poi') {
        handlePoiToolClick(point, mapSettings, onAddElement)
        return
      }

      if (activeTool === 'polygon' || activeTool === 'fixture-polygon') {
        // Apply angle snapping if shift is pressed and we have vertices
        let snappedPoint = point
        if (isShiftPressed && polygonVertices.length > 0) {
          const lastVertex = polygonVertices[polygonVertices.length - 1]
          snappedPoint = snapPointToAngle(lastVertex, point)
        }

        handlePolygonClick(
          snappedPoint,
          isPolygonDrawing,
          polygonVertices,
          activeTool,
          mapSettings,
          onAddElement,
          setIsPolygonDrawing,
          setPolygonVertices,
        )
        return
      }

      setIsDrawing(true)
      setDrawStart(point)
      // Always update ref for drawing
      mousePosRef.current = point

      // Throttle state updates for coordinate display (every 100ms)
      const now = Date.now()
      if (now - lastUpdateTime.current > 100) {
        setMousePos(point)
        lastUpdateTime.current = now
      }
    }

    // Throttle coordinate display updates
    const lastUpdateTime = useRef(0)

    const handleMouseMove = (e: React.MouseEvent) => {
      const point = getCanvasCoordinates(
        e.clientX,
        e.clientY,
        canvasRef.current,
        panOffset,
        zoom,
        elements,
        mapSettings,
      )
      // Always update ref for drawing
      mousePosRef.current = point

      // Throttle state updates for coordinate display (every 100ms)
      const now = Date.now()
      if (now - lastUpdateTime.current > 100) {
        setMousePos(point)
        lastUpdateTime.current = now
      }

      // Handle hover detection for handles (only in select mode)
      if (activeTool === 'select' && !isDragging) {
        let foundHover = false

        // Check if we're hovering over any labels first
        for (const element of elements) {
          const hasLabel =
            element.properties.roomName ||
            element.properties.label ||
            (element.type === 'fixture-rect' && element.properties.fixtureType)
          if (hasLabel && isPointInLabel(point, element)) {
            setHoveredElement(element)
            setHoveredHandle('label')
            foundHover = true
            break
          }
        }

        // If not hovering over label, check for other handles
        if (!foundHover) {
          for (const element of elements) {
            if (isLineBasedElement(element)) {
              const handle = getHandleAtPoint(
                point,
                element,
                setHoveredVertexIndex,
              )
              if (handle) {
                setHoveredElement(element)
                setHoveredHandle(handle)
                foundHover = true
                break
              }
            }
          }
        }

        if (!foundHover) {
          setHoveredElement(null)
          setHoveredHandle(null)
          setHoveredVertexIndex(-1)
        }
      }

      if (isPanning && panStart) {
        const deltaX = (e.clientX - panStart.x) / zoom
        const deltaY = (e.clientY - panStart.y) / zoom
        setPanOffset({
          x: panOffset.x + deltaX,
          y: panOffset.y + deltaY,
        })
        setPanStart({ x: e.clientX, y: e.clientY })
        return
      }

      if (isDragging && dragStart && dragElementId) {
        handleDragMove(
          point,
          dragStart,
          dragElementId,
          dragHandle,
          dragVertexIndex,
          elements,
          mapSettings,
          onUpdateElement,
          setDragStart,
          isShiftPressed,
        )
        return
      }

      if (isDrawing) {
        // Force redraw to show preview
        requestAnimationFrame(drawElements)
      }
    }

    const handleMouseUp = () => {
      // Reset middle mouse panning
      if (isMiddleMousePanning) {
        setIsMiddleMousePanning(false)
      }

      if (isPanning) {
        setIsPanning(false)
        setPanStart(null)
        return
      }

      if (isDragging) {
        setIsDragging(false)
        setDragStart(null)
        setDragElementId(null)
        setDragHandle(null)
        setDragVertexIndex(-1)
        return
      }

      if (
        isDrawing &&
        drawStart &&
        activeTool !== 'measure' &&
        activeTool !== 'polygon' &&
        activeTool !== 'fixture-polygon'
      ) {
        // Apply angle snapping if shift was pressed
        const finalPos = isShiftPressed
          ? snapPointToAngle(drawStart, mousePosRef.current)
          : mousePosRef.current

        const newElement = createElementFromDrawing(
          activeTool,
          drawStart,
          finalPos,
          mapSettings,
        )
        if (newElement) {
          onAddElement(newElement)
        }
        setIsDrawing(false)
        setDrawStart(null)
      }
    }

    const handleWheel = (e: React.WheelEvent) => {
      e.preventDefault()

      const canvas = canvasRef.current
      if (!canvas) return

      // Get mouse position relative to canvas
      const rect = canvas.getBoundingClientRect()
      const mouseX = e.clientX - rect.left
      const mouseY = e.clientY - rect.top

      // Calculate zoom factor
      const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1
      const newZoom = Math.max(0.1, Math.min(5, zoom * zoomFactor))

      // Calculate world position before zoom
      const worldX = (mouseX - panOffset.x) / zoom
      const worldY = (mouseY - panOffset.y) / zoom

      // Calculate new pan offset to keep mouse position stable
      const newPanX = mouseX - worldX * newZoom
      const newPanY = mouseY - worldY * newZoom

      setZoom(newZoom)
      setPanOffset({ x: newPanX, y: newPanY })
    }

    const handleContextMenu = (e: React.MouseEvent) => {
      e.preventDefault()

      // Only show context menu for polygon elements in select mode
      if (activeTool !== 'select') return

      const point = getCanvasCoordinates(
        e.clientX,
        e.clientY,
        canvasRef.current,
        panOffset,
        zoom,
        elements,
        mapSettings,
      )

      // Check if we right-clicked on a polygon
      for (const element of elements) {
        if (
          (element.type === 'polygon' || element.type === 'fixture-polygon') &&
          element.properties.vertices
        ) {
          const vertices = element.properties.vertices as Point[]

          // Check if we clicked on a vertex
          for (let i = 0; i < vertices.length; i++) {
            const vertex = vertices[i]
            const distance = Math.sqrt(
              Math.pow(point.x - vertex.x, 2) + Math.pow(point.y - vertex.y, 2),
            )

            if (distance <= 8) {
              // 8px threshold
              setContextMenu({
                x: e.clientX,
                y: e.clientY,
                elementId: element.id,
                vertexIndex: i,
              })
              return
            }
          }

          // Check if we clicked on an edge
          for (let i = 0; i < vertices.length; i++) {
            const v1 = vertices[i]
            const v2 = vertices[(i + 1) % vertices.length]

            if (isPointOnLine(point, v1, v2, 5)) {
              setContextMenu({
                x: e.clientX,
                y: e.clientY,
                elementId: element.id,
                vertexIndex: i,
                edgePoint: point,
              })
              return
            }
          }
        }
      }
    }

    // Handle keyboard events for polygon tool and arrow key nudging
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        // Track shift key state
        if (e.key === 'Shift') {
          setIsShiftPressed(true)
        }

        if (e.key === 'Escape' && isPolygonDrawing) {
          // Cancel polygon drawing
          setIsPolygonDrawing(false)
          setPolygonVertices([])
          return
        }

        // Arrow key nudging for selected elements
        if (selectedElement && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
          e.preventDefault() // Prevent page scrolling

          const nudgeAmount = 1 // 1px nudge
          let dx = 0, dy = 0

          switch (e.key) {
            case 'ArrowUp':
              dy = -nudgeAmount
              break
            case 'ArrowDown':
              dy = nudgeAmount
              break
            case 'ArrowLeft':
              dx = -nudgeAmount
              break
            case 'ArrowRight':
              dx = nudgeAmount
              break
          }

          // Check if a specific handle/vertex is selected
          if (selectedHandle === 'vertex' && selectedVertexIndex >= 0 &&
              (selectedElement.type === 'polygon' || selectedElement.type === 'fixture-polygon')) {
            // Nudge only the selected vertex for polygons
            if (selectedElement.properties.vertices) {
              const vertices = [...selectedElement.properties.vertices]
              if (vertices[selectedVertexIndex]) {
                vertices[selectedVertexIndex] = {
                  x: vertices[selectedVertexIndex].x + dx,
                  y: vertices[selectedVertexIndex].y + dy
                }
                onUpdateElement(selectedElement.id, {
                  properties: { ...selectedElement.properties, vertices }
                })
              }
            }
          } else if ((selectedHandle === 'controlPoint1' || selectedHandle === 'controlPoint2') &&
                     selectedVertexIndex >= 0 && selectedElement.curvedEdges) {
            // Nudge control points for curved edges
            const curvedEdges = [...(selectedElement.curvedEdges || [])]
            const edgeToUpdate = curvedEdges.find(e => e.edgeIndex === selectedVertexIndex)

            if (edgeToUpdate) {
              const updatedEdge = { ...edgeToUpdate }
              if (selectedHandle === 'controlPoint1') {
                updatedEdge.controlPoint1 = {
                  x: (edgeToUpdate.controlPoint1?.x || 0) + dx,
                  y: (edgeToUpdate.controlPoint1?.y || 0) + dy
                }
              } else {
                updatedEdge.controlPoint2 = {
                  x: (edgeToUpdate.controlPoint2?.x || 0) + dx,
                  y: (edgeToUpdate.controlPoint2?.y || 0) + dy
                }
              }

              const newCurvedEdges = curvedEdges.map(e =>
                e.edgeIndex === selectedVertexIndex ? updatedEdge : e
              )

              onUpdateElement(selectedElement.id, {
                curvedEdges: newCurvedEdges
              })
            }
          } else if (selectedHandle === 'start') {
            // Nudge start point for line-based elements
            onUpdateElement(selectedElement.id, {
              position: {
                x: selectedElement.position.x + dx,
                y: selectedElement.position.y + dy
              }
            })
          } else if (selectedHandle === 'end' && selectedElement.properties.endPosition) {
            // Nudge end point for line-based elements
            onUpdateElement(selectedElement.id, {
              properties: {
                ...selectedElement.properties,
                endPosition: {
                  x: selectedElement.properties.endPosition.x + dx,
                  y: selectedElement.properties.endPosition.y + dy
                }
              }
            })
          } else if (selectedHandle === 'vertex' && selectedVertexIndex >= 0) {
            // Handle vertex nudging for different element types
            if (selectedElement.type === 'circle' || selectedElement.type === 'fixture-circle') {
              // For circles, vertex index represents: 0=top, 1=right, 2=bottom, 3=left
              const radius = selectedElement.properties.radius || 0
              let newRadius = radius

              switch (selectedVertexIndex) {
                case 0: // top
                  newRadius = radius - dy
                  break
                case 1: // right
                  newRadius = radius + dx
                  break
                case 2: // bottom
                  newRadius = radius + dy
                  break
                case 3: // left
                  newRadius = radius - dx
                  break
              }

              if (newRadius > 5) { // Minimum radius
                onUpdateElement(selectedElement.id, {
                  properties: {
                    ...selectedElement.properties,
                    radius: newRadius
                  }
                })
              }
            } else if (
              selectedElement.type === 'room' ||
              selectedElement.type === 'rectangle' ||
              selectedElement.type === 'stairs' ||
              selectedElement.type === 'fixture-rect'
            ) {
              // For rectangles, vertex index represents: 0=nw, 1=ne, 2=se, 3=sw
              const { position, properties } = selectedElement
              const width = properties.width || 0
              const height = properties.height || 0

              switch (selectedVertexIndex) {
                case 0: // nw (top-left)
                  onUpdateElement(selectedElement.id, {
                    position: { x: position.x + dx, y: position.y + dy },
                    properties: {
                      ...properties,
                      width: width - dx,
                      height: height - dy
                    }
                  })
                  break
                case 1: // ne (top-right)
                  onUpdateElement(selectedElement.id, {
                    position: { x: position.x, y: position.y + dy },
                    properties: {
                      ...properties,
                      width: width + dx,
                      height: height - dy
                    }
                  })
                  break
                case 2: // se (bottom-right)
                  onUpdateElement(selectedElement.id, {
                    properties: {
                      ...properties,
                      width: width + dx,
                      height: height + dy
                    }
                  })
                  break
                case 3: // sw (bottom-left)
                  onUpdateElement(selectedElement.id, {
                    position: { x: position.x + dx, y: position.y },
                    properties: {
                      ...properties,
                      width: width - dx,
                      height: height + dy
                    }
                  })
                  break
              }
            }
          } else {
            // Default: Nudge the entire element
            const newPosition = {
              x: selectedElement.position.x + dx,
              y: selectedElement.position.y + dy
            }

            // Also update endPosition for line-based elements
            if (selectedElement.properties.endPosition) {
              onUpdateElement(selectedElement.id, {
                position: newPosition,
                properties: {
                  ...selectedElement.properties,
                  endPosition: {
                    x: selectedElement.properties.endPosition.x + dx,
                    y: selectedElement.properties.endPosition.y + dy
                  }
                }
              })
            } else if (selectedElement.properties.vertices) {
              // For polygons, move all vertices
              const vertices = selectedElement.properties.vertices.map((v: Point) => ({
                x: v.x + dx,
                y: v.y + dy
              }))
              onUpdateElement(selectedElement.id, {
                position: newPosition,
                properties: { ...selectedElement.properties, vertices }
              })
            } else {
              onUpdateElement(selectedElement.id, { position: newPosition })
            }
          }
        }
      }

      const handleKeyUp = (e: KeyboardEvent) => {
        // Track shift key state
        if (e.key === 'Shift') {
          setIsShiftPressed(false)
        }
      }

      document.addEventListener('keydown', handleKeyDown)
      document.addEventListener('keyup', handleKeyUp)
      return () => {
        document.removeEventListener('keydown', handleKeyDown)
        document.removeEventListener('keyup', handleKeyUp)
      }
    }, [isPolygonDrawing, selectedElement, selectedHandle, selectedVertexIndex, onUpdateElement])

    const handleAddVertex = useCallback(
      (elementId: string, afterIndex: number, newVertex: Point) => {
        addVertex(
          elementId,
          afterIndex,
          newVertex,
          elements,
          mapSettings,
          onUpdateElement,
          setContextMenu,
        )
      },
      [elements, mapSettings, onUpdateElement],
    )

    const handleRemoveVertex = useCallback(
      (elementId: string, vertexIndex: number) => {
        removeVertex(
          elementId,
          vertexIndex,
          elements,
          mapSettings,
          onUpdateElement,
          setContextMenu,
        )
      },
      [elements, mapSettings, onUpdateElement],
    )

    return (
      <CanvasContainer>
        <CanvasWrapper>
          <Canvas
            ref={canvasRef}
            className={getCursorClass(
              isDragging,
              activeTool,
              hoveredElement,
              hoveredHandle,
            )}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onContextMenu={handleContextMenu}
            onWheel={handleWheel}
          />
          <CanvasOverlay />
        </CanvasWrapper>

        <ZoomControls>
          <ZoomButton
            onClick={() => handleZoomIn(zoom, setZoom)}
            title="Zoom In">
            +
          </ZoomButton>
          <ZoomButton
            onClick={() => handleZoomOut(zoom, setZoom)}
            title="Zoom Out">
            −
          </ZoomButton>
        </ZoomControls>

        <CoordinatesDisplay>
          X: {Math.round(mousePosRef.current.x)}, Y: {Math.round(mousePosRef.current.y)} | Zoom:{' '}
          {Math.round(zoom * 100)}%
        </CoordinatesDisplay>

        <ContextMenu
          contextMenu={contextMenu}
          onAddVertex={handleAddVertex}
          onRemoveVertex={handleRemoveVertex}
        />
      </CanvasContainer>
    )
  },
)

export { MapCanvas }
